/**
 * PipelineByStage — horizontal opacity-graded blue bar list (v2 Dashboard §3.2).
 *
 * Replaces the spine's "Funnel conversion" bar chart. Does NOT port the
 * Offer→Accept conversion computation (it had a 200% data bug — spec §3.2).
 *
 * Faithfully implements the canonical `hbarChart()` from
 * design/v2-direction/index.html lines 1537–1554:
 *   • Horizontal bars, per-row height 34px, bar height 18px, radius 6px
 *   • Left label gutter: 118px wide, right-aligned, 11.5px, --muted-foreground
 *   • Track: full-width, --muted / 0.7
 *   • Fill: width proportional to count/max, opacity = 1 − i×0.11 (top=full,
 *     each lower bar steps 11% more transparent) — single blue hue only.
 *   • Value label: right of bar, 11.5px / 650, --foreground
 *
 * Chart discipline (spec §4): blue-forward, --chart-1 lead hue, NO saturated
 * blocks, NO multi-color series, muted descending opacity per row.
 *
 * Data source (FLAG 3.c): per-stage pipeline counts for a single role.
 * // GAP: useIntelligenceSummary does not expose per-stage pipeline counts.
 * // The component accepts a `stages` prop; DashboardV2 passes the closest
 * // available breakdown from the summary hook, falling back to canonical
 * // DASH_FUNNEL placeholder data with a gap comment. No backend change in PR.
 *
 * Token discipline: series color via CSS var token --chart-1. No hex. Guard-clean.
 */
import { cn } from "@/lib/utils";

interface PipelineStage {
  /** Stage label, e.g. "New", "Screened", "Interviewing". */
  label: string;
  /** Candidate count for this stage. */
  count: number;
}

interface PipelineByStageProps {
  stages: PipelineStage[];
  /**
   * Role title shown in the parent card subhead (FLAG 3.d — canonical: lead open role).
   * @deprecated Passed from DashboardV2 to document context; card head is rendered by parent.
   */
  roleTitle?: string;
  className?: string;
}

/** Row height as a constant (canonical 34px). */
const ROW_H = 34;
/** Bar height within the row (canonical 18px). */
const BAR_H = 18;
/** Left label gutter width (canonical 118px). */
const LAB_W = 118;
/** Opacity decrement per row (canonical 0.11). */
const OPACITY_STEP = 0.11;

export function PipelineByStage({
  stages,
  roleTitle: _roleTitle,  // consumed by parent card head, not rendered here
  className,
}: PipelineByStageProps): JSX.Element {
  const max = Math.max(...stages.map((s) => s.count), 1); // guard division by zero

  return (
    <div className={cn("flex flex-col gap-0", className)}>
      {stages.map((stage, i) => {
        const fillPct = (stage.count / max) * 100;
        // Opacity steps from 1.00 top to ~(1 - n*0.11) bottom.
        // Clamp to minimum 0.15 so the lowest bar stays visible.
        const opacity = Math.max(1 - i * OPACITY_STEP, 0.15);

        return (
          <div
            key={stage.label}
            className="flex items-center"
            style={{ height: `${ROW_H}px` }}
          >
            {/* Left label — right-aligned in 118px gutter */}
            <div
              className="shrink-0 text-right text-text-secondary"
              style={{
                width: `${LAB_W}px`,
                fontSize: "11.5px",
                paddingRight: "10px",
              }}
            >
              {stage.label}
            </div>

            {/* Bar track + fill */}
            <div className="relative flex-1" style={{ height: `${BAR_H}px` }}>
              {/* Track: full-width muted background */}
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: "color-mix(in srgb, var(--surface-subtle) 70%, transparent)" }}
              />
              {/* Fill: brand blue at declining opacity, width proportional to count */}
              {stage.count > 0 && (
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    width: `${Math.max(fillPct, 1.5)}%`,
                    background: `color-mix(in srgb, var(--chart-1) ${Math.round(opacity * 100)}%, transparent)`,
                  }}
                />
              )}
            </div>

            {/* Value label — right of bar */}
            <div
              className="shrink-0 tabular-nums text-text-primary"
              style={{
                fontSize: "11.5px",
                fontWeight: 650,
                paddingLeft: "8px",
                minWidth: "24px",
              }}
            >
              {stage.count}
            </div>
          </div>
        );
      })}
    </div>
  );
}
